// AnomaliesPanel.jsx
// Compact by default: shows only the single most recent + most severe anomaly.
// Clicking it expands into a scrollable worklist grouped by tower, with the day
// as a secondary heading inside each tower, cursor-based pagination ("Load
// more"), and per-row expansion. When there are no anomalies it shows an
// "all clear" empty state.
//
// A WORKLIST, NOT A LOG (#208). This list used to carry the description, the
// confidence, the weather line and the payload on every row, grouped by day.
// Most of that repeats from row to row — "Motion detected by PIR sensor" adds
// nothing to the word motion — and it pushed the one genuinely new thing on a
// busy evening below the fold. So:
//
//   * one line per entry by default: time, type, magnitude where one exists;
//   * grouped by TOWER, because crews are dispatched to places. Twelve events
//     at one tower is one job; twelve events across twelve towers is a bad
//     week, and grouping by day alone cannot tell those apart;
//   * runs of the same finding on one device arrive already folded by the API
//     (#211) and show as a span and a count, with the folded rows inside the
//     expansion;
//   * everything that used to be on the row is still there, one tap down.
//
// Data source: GET {apiBaseUrl}/api/anomalies?limit&nextToken&include_low
//   → { anomalies: [{ id, device_id, node_id, node_label, timestamp,
//                     first_timestamp, sensor_type, raw_payload,
//                     severity, confidence, description,
//                     weather_correlated, weather_summary,
//                     count, members }], nextToken }
//
// Since #54 this comes from the SamstAnomalies table, so severity/confidence/
// description are the detector's own values rather than re-derived here, and
// temperature and motion anomalies appear alongside tilt. Low-severity rows
// (every PIR motion trigger) are excluded server-side unless the user asks for
// them — see the motion toggle below.
//
// Auth tiers (issue #34): `simulated` replaces that endpoint with generated
// anomalies and issues no request; `lockedForGuest` additionally truncates the
// expanded list to one card behind a login prompt.

import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Collapse, IconButton, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import SensorsIcon from "@mui/icons-material/Sensors";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import FloodOutlinedIcon from "@mui/icons-material/FloodOutlined";
import { generateFakeAnomalies } from "../fakeData";
import { apiFetch } from "../apiFetch";
import HelpTip from "./HelpTip";

const PAGE_SIZE = 20;
// The collapsed summary ranks by severity only within this many of the most
// recent entries, so paging in older history can never promote a stale anomaly.
const TOP_WINDOW = 20;
const AXIS_KEYS = ["threshold_x", "threshold_y", "threshold_z"];
const AXIS_ON = "#ef4444"; // tripped axis / label — red
const AXIS_OFF = "#9ca3af"; // idle axis / label — grey

// Detector severity, ranked for ordering. Unknown/missing sorts above "low" so
// a row whose severity the detector never set is not treated as the least
// interesting thing on the list.
const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 0 };
const UNKNOWN_SEVERITY_RANK = 1;

function severityRank(item) {
  const s = String(item.severity ?? "").toLowerCase();
  return SEVERITY_RANK[s] ?? UNKNOWN_SEVERITY_RANK;
}

// Detector confidence as a whole percentage, or null when the row has none.
// The Lambda writes it as a stringified float ("0.8"), and legacy rows omit it
// entirely — an unparseable value must render nothing rather than "NaN%".
function confidencePercent(item) {
  const value = parseFloat(item.confidence);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

// Badge colours per severity. Anything unrecognised falls back to grey rather
// than rendering an unstyled badge.
const SEVERITY_STYLE = {
  critical: { bg: "#fef2f2", fg: "#991b1b", border: "#fecaca" },
  high:     { bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" },
  medium:   { bg: "#fefce8", fg: "#854d0e", border: "#fde68a" },
  low:      { bg: "#f3f4f6", fg: "#4b5563", border: "#e5e7eb" },
};

function SeverityBadge({ severity }) {
  if (!severity) return null;
  const key = String(severity).toLowerCase();
  const s = SEVERITY_STYLE[key] ?? SEVERITY_STYLE.low;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        color: s.fg,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 4,
        padding: "1px 5px",
        flexShrink: 0,
      }}
    >
      {key}
    </span>
  );
}

// How many rows the server folded into this entry (#211).
//
// Only rendered when the entry really is a roll-up. A "x1" badge on every
// ordinary row would be noise, and the API deliberately gives rolled and
// un-rolled entries the same shape (`count: 1`) so the panel tests one field
// rather than two.
//
// The span the fold covers is no longer in this tooltip: since #208 the time
// column of a rolled-up row IS the span, so repeating it here would be the
// same fact twice, once where nobody can see it.
//
// `variant_count` is shown when the fold covered more than one wording. The
// server groups by device, type and severity rather than by text — a loose
// sensor trips a different combination of axes almost every reading, so keying
// on the text grouped almost nothing — and this is what keeps that visible
// instead of implicit. The title attribute carries the detail for anyone who
// wants it without spending a line of the card on it.
function RepeatBadge({ item, tr }) {
  const count = item.count ?? 1;
  if (count <= 1) return null;

  const variants = item.variant_count ?? 1;

  const title = [
    tr("rollupTitle", `${count} readings folded into one entry`, { count }),
    variants > 1 ? tr("rollupVariants", `${variants} different messages`, { count: variants }) : null,
    item.truncated ? tr("rollupTruncated", "More continue past this page") : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <span
      title={title}
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: "#374151",
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
        borderRadius: 4,
        padding: "1px 5px",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      ×{count}
      {item.truncated ? "+" : ""}
      {variants > 1 ? ` · ${variants}` : ""}
    </span>
  );
}

// Which of the X/Y/Z axes tripped their threshold on this reading.
function trippedAxes(item) {
  const p = item.raw_payload || {};
  return {
    x: p.threshold_x === "trigger",
    y: p.threshold_y === "trigger",
    z: p.threshold_z === "trigger",
  };
}

// Human-friendly day label: "Today", "Yesterday", "Jul 20", "Jul 20, 2025"
function dayLabel(date, t) {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return t ? t("today") : "Today";
  if (diffDays === 1) return t ? t("yesterday") : "Yesterday";
  const opts = { month: "short", day: "numeric" };
  if (date.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return date.toLocaleDateString(undefined, opts);
}

/**
 * The Date inside an anomaly's timestamp, whatever shape it arrives in.
 *
 * NO ANOMALY MAY EVER RENDER AS "Unknown date" (#215). Every row in
 * SamstAnomalies is keyed on its timestamp — DynamoDB cannot store one without
 * a sort key — so the instant is always there, and a panel that cannot show it
 * is a bug in the panel, not a row with no date.
 *
 * What gets in the way: a detector whose row would collide with the threshold
 * row from the same reading appends a tag to the key (`…Z#ml`, `…Z#mount`), and
 * `new Date()` returns NaN on that. The API already cuts it off, and this cuts
 * it off again. That duplication is deliberate. The first fix lived only in the
 * API, a new detector appeared, and "Unknown date" came straight back — so the
 * screen the operator actually reads now defends itself instead of trusting the
 * layer above it.
 *
 * Returns null only for a genuinely empty value, which no stored row has.
 */
function parseAnomalyDate(timestamp) {
  if (!timestamp) return null;
  const text = String(timestamp);
  const direct = new Date(text);
  if (!isNaN(direct.getTime())) return direct;
  // Everything before the first '#' is the time, by the table's own convention.
  const cut = text.indexOf("#");
  if (cut > 0) {
    const stripped = new Date(text.slice(0, cut));
    if (!isNaN(stripped.getTime())) return stripped;
  }
  return null;
}

/**
 * Group anomalies (already sorted newest-first) by TOWER, then by day (#208).
 *
 * Towers come out in order of their most recent event and the rows inside one
 * keep that order, both of which fall out of a single pass over a newest-first
 * list. Day buckets can be compared against the last one only — within a tower
 * the stream is still monotonically descending in time, so same-day rows are
 * always adjacent.
 *
 * The heading is the node's operator-facing label. A device on no node heads
 * its own group with its device id, which is true; inventing "Unknown tower"
 * would file several unrelated devices under one heading and read as a place.
 *
 * Grouped by node_id and only LABELLED with the label: an operator types those
 * labels, and two towers that happen to be called the same thing are still two
 * towers and two vehicles.
 */
function groupByTower(items, t) {
  const towers = [];
  const byKey = new Map();
  for (const item of items) {
    const key = item.node_id || item.device_id || "";
    let tower = byKey.get(key);
    if (!tower) {
      const label = item.node_label || item.node_id || item.device_id || "";
      tower = { key, label, devices: new Set(), days: [], entries: 0 };
      byKey.set(key, tower);
      towers.push(tower);
    }
    tower.devices.add(item.device_id);
    tower.entries += 1;

    const d = parseAnomalyDate(item.timestamp);
    // The fallback is reachable only if a row arrived with no timestamp at all,
    // which the table cannot produce. It stays as a last resort rather than
    // rendering the string "null" as a heading.
    const label = d ? dayLabel(d, t) : (t ? t("unknownDate") : "Unknown date");
    let day = tower.days[tower.days.length - 1];
    if (!day || day.label !== label) {
      day = { label, items: [] };
      tower.days.push(day);
    }
    day.items.push(item);
  }
  return towers;
}

function formatTime(timestamp) {
  const ts = parseAnomalyDate(timestamp);
  // Never the raw string: printing `2026-10-27T00:00:00.000Z#mount` where a
  // clock time belongs is the same defect as the day heading, one line down.
  return ts ? ts.toLocaleString() : "";
}

function formatClock(date) {
  return date ? date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
}

/**
 * The time column of a worklist row: one clock time, or the span a rolled-up
 * entry covers.
 *
 * "192 triggers, 18:00 to 21:00" is one fact, and the span is where it reads as
 * one. A span that crosses midnight collapses back to the newest time, because
 * the row sits under a day heading that would then be contradicted by its own
 * left-hand column — the full range stays in the `title` and is spelled out in
 * the expansion.
 */
function rowTime(item) {
  const last = parseAnomalyDate(item.timestamp);
  if (!last) return { text: "", title: "" };
  const first = parseAnomalyDate(item.first_timestamp);
  if (!first || first.getTime() === last.getTime()) {
    return { text: formatClock(last), title: last.toLocaleString() };
  }
  const sameDay = first.toDateString() === last.toDateString();
  return {
    text: sameDay ? `${formatClock(first)}–${formatClock(last)}` : formatClock(last),
    title: `${first.toLocaleString()} – ${last.toLocaleString()}`,
  };
}

// What the row calls each kind of finding. `anomaly_type` is the detector's own
// vocabulary and reads like a column name; "motion" is what an operator calls a
// PIR trigger. ml_anomaly is scored from the tilt drift features, so it is a
// tilt finding by a second detector rather than a different sensor (#102).
const TYPE_LABELS = {
  tilt: ["anomalyTypeTilt", "tilt"],
  ml_anomaly: ["anomalyTypeMl", "tilt (ML)"],
  temperature: ["anomalyTypeTemperature", "temp"],
  pir: ["anomalyTypeMotion", "motion"],
  mounting: ["anomalyTypeMounting", "mounting"],
  flood: ["anomalyTypeFlood", "flood"],
};

function typeLabel(item, tr) {
  const type = String(item.sensor_type ?? "").toLowerCase();
  const entry = TYPE_LABELS[type];
  // An unrecognised type prints itself rather than nothing. A detector added
  // tomorrow should still produce a legible row, and an empty type column reads
  // as "nothing is known about this" when plenty is.
  return entry ? tr(entry[0], entry[1]) : type;
}

// A degree figure inside a sentence the detector wrote. See rowMagnitude.
const DEGREES_RE = /([+-]?\d+(?:\.\d+)?)\s*°/g;

/**
 * The largest degree figure in a description, signed, to one decimal.
 *
 * Largest by absolute value: "drift x=+0.12° y=-0.03° z=+1.20°, magnitude
 * 1.23°" is one finding with one headline number, and the biggest excursion is
 * it.
 */
function largestDegrees(description) {
  if (!description) return null;
  let best = null;
  for (const match of String(description).matchAll(DEGREES_RE)) {
    const value = parseFloat(match[1]);
    if (!Number.isFinite(value)) continue;
    if (best === null || Math.abs(value) > Math.abs(best)) best = value;
  }
  if (best === null) return null;
  return `${best > 0 ? "+" : ""}${best.toFixed(1)}°`;
}

/**
 * The number on a one-line row — "how far", not "that something happened".
 *
 * Where each kind of row gets it, and why not from somewhere simpler:
 *
 *   temperature — straight off the payload, which holds the reading itself.
 *   tilt        — the axes that tripped their hardware threshold, which is what
 *                 the device actually reported. NOT the angles in the payload:
 *                 those are raw mount angles, and a mast resting at -82° has
 *                 not moved 82°. lambda_function.py's build_description had to
 *                 fix exactly that mistake once already.
 *   tilt, drift — no threshold tripped means the finding is a deviation from
 *                 the device's learned baseline, and the baseline is not in the
 *                 row. The detector puts the figure in its sentence and nowhere
 *                 else, so it is read back out of the sentence.
 *   pir         — none. A motion trigger has no magnitude; the type is the fact.
 *
 * Presentation only, and it fails soft: no number, no magnitude column, and the
 * detector's full sentence is one tap away either way.
 */
function rowMagnitude(item) {
  const type = String(item.sensor_type ?? "").toLowerCase();
  if (type === "pir" || type === "mounting" || type === "flood") return null;

  if (type === "temperature") {
    // raw_payload values are stringified by the Lambda, so parse before
    // formatting — `p.temperature + "°C"` would concatenate rather than round.
    const temp = parseFloat((item.raw_payload || {}).temperature);
    return Number.isFinite(temp) ? `${temp.toFixed(1)}°C` : null;
  }

  const on = trippedAxes(item);
  const tripped = ["x", "y", "z"].filter((a) => on[a]).map((a) => a.toUpperCase());
  if (tripped.length > 0) return tripped.join(",");

  return largestDegrees(item.description);
}

/**
 * The description, when it belongs on the collapsed row.
 *
 * Four of the five sentences the detectors can produce restate what the row
 * already shows: "Motion detected by PIR sensor" beside the word motion,
 * "Temperature out of range: 62.4°C" beside 62.4°C, and both tilt wordings
 * beside the axes or the drift figure. The fifth is different in kind — a
 * mounting finding is an instruction to go and inspect, and the sentence IS the
 * finding, not a gloss on it — so that is the one that earns the space.
 */
function inlineDescription(item) {
  // A flood watch (#244) is the same kind of row: the sentence names which
  // evidence fired and whether a satellite confirmed it, and nothing else on
  // the row says either.
  const type = String(item.sensor_type ?? "").toLowerCase();
  return type === "mounting" || type === "flood" ? item.description || null : null;
}

// Severity on a one-line row.
//
// The full badge is up to eight characters of uppercase and was the widest
// thing on the row once the description came off it. The word itself is still
// on the expanded card, and title/aria-label carry it here, so colour is never
// the only way to read the severity.
function SeverityDot({ severity }) {
  const key = String(severity ?? "").toLowerCase();
  const s = SEVERITY_STYLE[key] ?? SEVERITY_STYLE.low;
  return (
    <Box
      component="span"
      role="img"
      aria-label={key ? `severity ${key}` : "severity unknown"}
      title={key || "unknown"}
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: s.fg,
        border: `1px solid ${s.border}`,
      }}
    />
  );
}

// Small 3D coordinate glyph. Each axis and its letter turns red when that
// axis tripped its threshold; idle axes stay grey.
function AxisGlyph({ item, size = 40 }) {
  const on = trippedAxes(item);
  const OX = 15;
  const OY = 31; // origin
  const label = ["x", "y", "z"].filter((a) => on[a]).map((a) => a.toUpperCase()).join(", ");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label={label ? `Axes triggered: ${label}` : "No axes triggered"}
    >
      {/* Z axis — up */}
      <line x1={OX} y1={OY} x2={OX} y2={9} stroke={on.z ? AXIS_ON : AXIS_OFF} strokeWidth="2" strokeLinecap="round" />
      {/* X axis — right */}
      <line x1={OX} y1={OY} x2={39} y2={OY} stroke={on.x ? AXIS_ON : AXIS_OFF} strokeWidth="2" strokeLinecap="round" />
      {/* Y axis — depth (up-right) */}
      <line x1={OX} y1={OY} x2={34} y2={15} stroke={on.y ? AXIS_ON : AXIS_OFF} strokeWidth="2" strokeLinecap="round" />
      {/* origin marker */}
      <circle cx={OX} cy={OY} r="1.6" fill="#6b7280" />
      {/* axis labels */}
      <text x={OX} y={7} fontSize="10" fontWeight="700" textAnchor="middle" fill={on.z ? AXIS_ON : AXIS_OFF}>Z</text>
      <text x={44} y={OY + 3} fontSize="10" fontWeight="700" textAnchor="middle" fill={on.x ? AXIS_ON : AXIS_OFF}>X</text>
      <text x={39} y={12} fontSize="10" fontWeight="700" textAnchor="middle" fill={on.y ? AXIS_ON : AXIS_OFF}>Y</text>
    </svg>
  );
}

// The axis glyph only means anything for tilt. Since #54 the panel also lists
// temperature and motion anomalies, and on those rows three grey axes read as
// "nothing triggered" rather than "not applicable" — so they get their own mark.
// High and critical rows draw it in the same red the tilt glyph uses, so urgency
// is legible at a glance regardless of sensor type.
function AnomalyGlyph({ item, size = 40 }) {
  const type = String(item.sensor_type ?? "").toLowerCase();
  const p = item.raw_payload || {};
  const hasAxes = AXIS_KEYS.some((k) => k in p);
  // Legacy rows may carry no anomaly_type; the payload still identifies tilt.
  if (type === "tilt" || (!type && hasAxes)) return <AxisGlyph item={item} size={size} />;

  let Icon = SensorsIcon;
  let label = type ? `${type} anomaly` : "anomaly";
  let caption = null;
  if (type === "mounting") {
    // A "Check mounting" finding (#211) is an instruction to go and inspect,
    // not a reading, so it gets a tool rather than a sensor glyph. It is the
    // only entry in this panel that asks for a physical action.
    Icon = BuildOutlinedIcon;
    label = "check mounting";
  } else if (type === "flood") {
    // A flood watch or a satellite-confirmed flood near the node (#244). About
    // the site, not a reading, so it gets its own mark rather than a sensor.
    Icon = FloodOutlinedIcon;
    label = "flood watch";
  } else if (type === "temperature") {
    Icon = DeviceThermostatIcon;
    label = "temperature anomaly";
    // raw_payload values are stringified by the Lambda, so parse before
    // formatting — `p.temperature + "°"` would concatenate rather than round.
    const temp = parseFloat(p.temperature);
    if (Number.isFinite(temp)) caption = `${temp.toFixed(1)}°`;
  } else if (type === "pir") {
    Icon = DirectionsRunIcon;
    label = "motion detected";
  }

  return (
    <Box
      role="img"
      aria-label={label}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: severityRank(item) >= SEVERITY_RANK.high ? AXIS_ON : AXIS_OFF,
      }}
    >
      <Icon sx={{ fontSize: size * (caption ? 0.5 : 0.62) }} />
      {caption && (
        <Box
          component="span"
          sx={{ fontSize: Math.max(9, size * 0.22), fontWeight: 700, lineHeight: 1 }}
        >
          {caption}
        </Box>
      )}
    </Box>
  );
}

/**
 * One entry of the worklist: a single line, with everything else behind it.
 *
 * The collapsed line is time · type · magnitude (plus the device id where a
 * tower has more than one sensor). The expansion is the card this panel used to
 * render on every row — the detector's sentence, its confidence, the weather
 * context and the raw payload — plus, on a rolled-up entry, the rows that were
 * folded into its count.
 */
function WorklistRow({ item, isOpen, onToggle, showDevice, tr, t, simulated }) {
  // Where a timestamp becomes text. Same rule as the day heading: a clock time
  // or nothing, never the raw sort key (#215).
  const time = rowTime(item);
  const magnitude = rowMagnitude(item);
  const inlineDesc = inlineDescription(item);
  const members = item.members ?? [];
  const count = item.count ?? 1;
  // The total the folded block reports, carrying the same "+" RepeatBadge puts
  // on a truncated entry. One value for both the translated string and the
  // English fallback — `tr` returns the fallback verbatim when no `t` is
  // supplied, so interpolating the bare count there would print an uncapped
  // number on exactly the path that has no translator to correct it.
  const foldedTotal = item.truncated ? `${count}+` : count;

  return (
    <Box sx={{ borderBottom: "1px solid #f3f4f6" }}>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          py: 0.4,
          cursor: "pointer",
          minWidth: 0,
          "&:hover": { bgcolor: "#f9fafb" },
        }}
      >
        <SeverityDot severity={item.severity} />
        <Typography
          variant="caption"
          title={time.title}
          sx={{ color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {time.text}
        </Typography>
        {showDevice && (
          <Typography
            variant="caption"
            title={item.device_id}
            sx={{
              color: "text.disabled",
              maxWidth: 72,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 0,
            }}
          >
            {item.device_id}
          </Typography>
        )}
        <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
          {typeLabel(item, tr)}
        </Typography>
        {magnitude && (
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
            {magnitude}
          </Typography>
        )}
        {inlineDesc && (
          <Typography
            variant="caption"
            title={inlineDesc}
            sx={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {inlineDesc}
          </Typography>
        )}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <RepeatBadge item={item} tr={tr} />
          {/* Only a strict `true` shows the mark: the field is tri-state and
              null means the weather could not be checked, which is not the same
              as calm (#64). The unknown case is spelled out in the expansion
              rather than marking every row during an outage. */}
          {item.weather_correlated === true && (
            <Box component="span" role="img" aria-label="weather event" title={item.weather_summary ?? "weather event"} sx={{ fontSize: 11 }}>
              🌩
            </Box>
          )}
          {isOpen ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          )}
        </Box>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ px: 1, py: 0.75, bgcolor: "#f5f5f5", borderTop: "1px solid #e5e7eb" }}>
          {/* The device and the glyph move here from the collapsed row. The
              axis glyph says which axes tripped on the reading, which is detail
              about one event — exactly what the expansion is for. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, minWidth: 0 }}>
            <AnomalyGlyph item={item} size={34} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {item.device_id}
                </Typography>
                <SeverityBadge severity={item.severity} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {time.title}
              </Typography>
            </Box>
          </Box>

          {/* The detector's own explanation, e.g. "Y axis hardware threshold
              triggered; Z axis drift -10.8°". Absent on rows written before the
              Lambda recorded one. */}
          {item.description && (
            <Typography variant="caption" sx={{ display: "block", mb: 0.5, lineHeight: 1.35 }}>
              {item.description}
            </Typography>
          )}

          {/* Detector metadata. confidence is a stringified float from the
              Lambda ("0.8"); legacy rows have neither field. */}
          {(confidencePercent(item) !== null || item.sensor_type) && (
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 0.5 }}>
              {item.sensor_type && <>{item.sensor_type}</>}
              {item.sensor_type && confidencePercent(item) !== null && " · "}
              {confidencePercent(item) !== null && (
                <>
                  {tr("confidence", "confidence")} {confidencePercent(item)}%
                  {/* 0.8 is not a probability and was never calibrated against
                      outcomes. The card is already expanded here, so the control
                      is beside the number rather than behind a second tap. */}
                  <HelpTip topic="anomalyConfidence" t={t} simulated={simulated} />
                </>
              )}
            </Typography>
          )}

          {item.weather_correlated === true && item.weather_summary && (
            <Typography variant="caption" sx={{ display: "block", color: "#854d0e", mb: 0.5 }}>
              🌩 {item.weather_summary}
            </Typography>
          )}
          {/* The weather lookup failed for this detection, so the absent mark
              above means "unknown", not "no weather" (#64). Rows written before
              weather_status existed carry null and say nothing, as before. */}
          {item.weather_status && item.weather_status !== "ok" && (
            <Typography variant="caption" sx={{ display: "block", color: "text.disabled", mb: 0.5 }}>
              {tr("weatherNotChecked", "Weather not checked for this detection")}
            </Typography>
          )}

          {/* What the count is made of. "×192" is a claim, and an operator who
              cannot open it has to take it on trust — so the rows the server
              folded travel with the entry and are listed here.
              The sample is capped server-side, and the cap says so rather than
              letting a partial list read as the whole run.

              `count` carries the same "+" RepeatBadge puts on a truncated
              entry, and for the same reason. On a device that trips all
              evening the run reaches past the page's own scan budget, so the
              total is itself a floor: em320-tilt-016 wrote 3,424 rows between
              18:00 and 21:00 on 2026-10-25, of which a page reads at most 500.
              Without the "+" this line reads "newest 20 of 500" for a run of
              3,424 — a capped number presented as a whole one, which is the
              exact thing the line exists to prevent. */}
          {count > 1 && members.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                {tr("rollupFolded", "Readings folded into this row")}
                {members.length < count && (
                  <>
                    {" · "}
                    {tr("rollupShowing", `newest ${members.length} of ${foldedTotal}`, {
                      shown: members.length,
                      count: foldedTotal,
                    })}
                  </>
                )}
              </Typography>
              <Box sx={{ maxHeight: 150, overflowY: "auto", mt: 0.25 }}>
                {members.map((member, i) => (
                  <Typography
                    key={`${member.timestamp}-${i}`}
                    variant="caption"
                    sx={{ display: "flex", gap: 0.75, lineHeight: 1.4, color: "text.secondary" }}
                  >
                    <Box component="span" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {formatClock(parseAnomalyDate(member.timestamp))}
                    </Box>
                    <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {member.description}
                    </Box>
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {tr("rawPayload", "Raw payload")}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              fontSize: 11,
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(item.raw_payload, null, 2)}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// How many fake anomalies the non-superuser tiers see.
const FAKE_ANOMALY_COUNT = 5;

// `compact` tightens the collapsed summary for the mobile layout, where this
// widget sits between the chip row and the map and every pixel it takes is map
// the operator cannot see. It only affects spacing and type scale — no content
// is dropped except the "click to view all" hint, which is redundant next to the
// chevron on a touch screen.
export default function AnomaliesPanel({ apiBaseUrl, t, simulated = false, lockedForGuest = false, compact = false }) {
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true); // initial page load
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [panelExpanded, setPanelExpanded] = useState(false); // collapsed by default
  const [expandedId, setExpandedId] = useState(null); // which card shows its raw payload
  // Motion triggers are severity "low" and vastly outnumber everything else, so
  // they are off by default and fetched only when switched on (#54).
  const [includeLow, setIncludeLow] = useState(false);

  const tr = (key, fallback, vars) => (t ? t(key, vars) : fallback);

  const fetchPage = useCallback(
    async (token) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (token) params.set("nextToken", token);
      if (includeLow) params.set("include_low", "1");
      const res = await apiFetch(`${apiBaseUrl}/api/anomalies?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    [apiBaseUrl, includeLow]
  );

  // Load the first page only — keeps the panel fast on open.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    if (simulated) {
      // Synchronous — nothing in flight, so no cleanup is needed. The low
      // filter is applied here too, so the motion toggle behaves the same way
      // for the tiers that never call the API.
      const fake = generateFakeAnomalies(FAKE_ANOMALY_COUNT);
      setItems(
        includeLow ? fake : fake.filter((a) => String(a.severity).toLowerCase() !== "low")
      );
      setNextToken(null);
      setLoading(false);
      return undefined;
    }
    fetchPage(null)
      .then((data) => {
        if (cancelled) return;
        setItems(data.anomalies ?? []);
        setNextToken(data.nextToken ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching anomalies:", err);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage, simulated, includeLow]);

  const handleLoadMore = () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    fetchPage(nextToken)
      .then((data) => {
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          const fresh = (data.anomalies ?? []).filter((i) => !seen.has(i.id));
          return [...prev, ...fresh];
        });
        setNextToken(data.nextToken ?? null);
      })
      .catch((err) => {
        console.error("Error loading more anomalies:", err);
      })
      .finally(() => setLoadingMore(false));
  };

  // Newest first. Timestamps are the DynamoDB SK — ISO-8601 UTC strings with
  // nanosecond precision ("2026-07-25T10:34:00.998190742Z") — so lexicographic
  // comparison is exact. Date-based subtraction would truncate to milliseconds
  // and collapse sub-millisecond entries into arbitrary ties.
  const byRecency = [...items].sort((a, b) =>
    a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0
  );

  // "Most recent and most severe": pick the highest severity among only the
  // TOP_WINDOW newest entries. Reducing over every loaded item meant each
  // "Load more" widened the candidate pool backwards in time, so a severe but
  // months-old anomaly would take over the collapsed summary. byRecency is
  // newest-first and the comparison is strict, so severity ties keep the
  // more recent entry.
  const topAnomaly = byRecency
    .slice(0, TOP_WINDOW)
    .reduce((best, cur) => (best === null || severityRank(cur) > severityRank(best) ? cur : best), null);

  // Guests get a single teaser card; the rest of the history sits behind the
  // login prompt rendered underneath the list.
  const visible = lockedForGuest ? byRecency.slice(0, 1) : byRecency;

  const towers = groupByTower(visible, t);
  // We only hold the first page(s); if more exist show the count as "N+".
  const countLabel = nextToken ? `${items.length}+` : `${items.length}`;
  // "+N more" beyond the one shown in the collapsed summary.
  const moreCount = nextToken ? `${items.length - 1}+` : `${items.length - 1}`;

  // The help belongs on the panel title rather than on each severity badge:
  // what a reader needs is the difference between the two detectors and the
  // fact that `medium` raises nothing, and that is one claim about the list,
  // not one per row (#149, #152).
  const title = (
    <Typography
      variant={compact ? "subtitle2" : "subtitle1"}
      sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.25 }}
    >
      {tr("detectedAnomalies", "Detected Anomalies")}
      <HelpTip topic="mlAnomaly" t={t} simulated={simulated} />
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ minWidth: compact ? 0 : 260, maxWidth: compact ? "100%" : 340 }}>
        {title}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {tr("loading", "Loading...")}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minWidth: compact ? 0 : 260, maxWidth: compact ? "100%" : 340 }}>
        {title}
        <Typography variant="body2" color="error.main">
          {tr("anomaliesError", "Failed to load anomalies.")}
        </Typography>
      </Box>
    );
  }

  if (byRecency.length === 0) {
    // Empty state — everything is healthy.
    return (
      <Box sx={{ minWidth: compact ? 0 : 260, maxWidth: compact ? "100%" : 340 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 2,
            px: 1,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#22c55e", mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#16a34a" }}>
            {tr("allSystemsNormal", "All systems normal")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tr("noAnomalies", "No anomalies detected")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 260, maxWidth: 340 }}>
      {/* Header row — title, count badge, and expand/collapse toggle */}
      <Box
        onClick={() => setPanelExpanded((e) => !e)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {title}
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.75,
              borderRadius: 10,
              bgcolor: "#ef4444",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {countLabel}
          </Box>
        </Box>
        <IconButton size="small" aria-label={panelExpanded ? "collapse list" : "expand list"}
          sx={compact ? { p: 0.25, ml: 0.5 } : undefined}>
          {panelExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {!panelExpanded ? (
        // Collapsed — show only the single most recent + most severe anomaly.
        <Box
          onClick={() => setPanelExpanded(true)}
          sx={{
            mt: compact ? 0.25 : 0.5,
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            p: compact ? 0.75 : 1,
            cursor: "pointer",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* The tower, for the same reason the list groups by it: the first
                thing an operator needs from a single-line summary is where.
                The device id is in the title here and on the row's expansion
                below (#208). Devices on no node still show their own id. */}
            <Typography
              variant="body2"
              title={topAnomaly.device_id}
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {topAnomaly.node_label || topAnomaly.device_id}
            </Typography>
            <SeverityBadge severity={topAnomaly.severity} />
            <Box sx={{ ml: "auto", flexShrink: 0, display: "flex", alignItems: "center" }}>
              <AnomalyGlyph item={topAnomaly} size={compact ? 30 : 44} />
            </Box>
          </Box>
          {/* The detector's explanation replaces nothing — it is added above the
              timestamp. Compact clips it to one line so the widget's height on
              mobile does not depend on how wordy the detector was. */}
          {topAnomaly.description && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.25,
                lineHeight: 1.3,
                ...(compact
                  ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
                  : {}),
              }}
            >
              {topAnomaly.description}
            </Typography>
          )}
          {/* Compact folds the timestamp and the "+N more" count onto one line;
              the full layout keeps them stacked with the explicit hint. */}
          {compact ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0 }}>
              {formatTime(topAnomaly.timestamp)}
              {topAnomaly.weather_correlated && " · 🌩"}
              {items.length > 1 && (
                <Box component="span" sx={{ color: "#1976d2", fontWeight: 600 }}>
                  {` · ${tr("moreCount", `+${moreCount} more`, { n: moreCount })}`}
                </Box>
              )}
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {formatTime(topAnomaly.timestamp)}
                {topAnomaly.weather_correlated && " · 🌩"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#1976d2" }}>
                {items.length > 1
                  ? `${tr("moreCount", `+${moreCount} more`, { n: moreCount })} · ${tr("clickToExpand", "click to view all")}`
                  : tr("clickToExpand", "click to view all")}
              </Typography>
            </>
          )}
        </Box>
      ) : (
        // Expanded — full scrollable, grouped, paginated list.
        <>
          {/* Motion events are severity "low" and outnumber structural
              anomalies by roughly an order of magnitude, so they are opt-in.
              Toggling refetches from page one — mixing a filtered first page
              with an unfiltered second would corrupt the cursor sequence. */}
          {!lockedForGuest && (
            <Box
              onClick={() => { setIncludeLow((v) => !v); setExpandedId(null); }}
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
                userSelect: "none",
                color: includeLow ? "#1976d2" : "text.secondary",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 16,
                  borderRadius: 8,
                  bgcolor: includeLow ? "#1976d2" : "#d1d5db",
                  position: "relative",
                  transition: "background-color 0.15s",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    position: "absolute",
                    top: 2,
                    left: includeLow ? 14 : 2,
                    transition: "left 0.15s",
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {tr("showMotionEvents", "Show motion events")}
              </Typography>
            </Box>
          )}

          <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 0.5, mt: 0.5 }}>
            {towers.map((tower) => (
              <Box key={tower.key} sx={{ mb: 1.25 }}>
                {/* The tower is the primary heading because that is the unit of
                    work: a crew goes to a place. The day heading below it is
                    what the list used to be grouped by, now secondary (#208). */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    position: "sticky",
                    top: 0,
                    bgcolor: "white",
                    py: 0.5,
                    borderBottom: "1px solid #e5e7eb",
                    zIndex: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    title={[...tower.devices].join(", ")}
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tower.label}
                  </Typography>
                  <Box
                    component="span"
                    title={tr("towerEntries", `${tower.entries} entries at this tower`, { n: tower.entries })}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#4b5563",
                      bgcolor: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: 4,
                      px: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {tower.entries}
                  </Box>
                </Box>
                {tower.days.map((day) => (
                  <Box key={day.label}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      {day.label}
                    </Typography>
                    {day.items.map((item) => (
                      <WorklistRow
                        key={item.id}
                        item={item}
                        isOpen={expandedId === item.id}
                        onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                        // The device id earns a column only where it
                        // distinguishes something. One tower with one sensor
                        // repeating it on every row is the noise this issue is
                        // about; a tower carrying tilt and PIR needs it.
                        showDevice={tower.devices.size > 1}
                        tr={tr}
                        t={t}
                        simulated={simulated}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {lockedForGuest && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: 20 }}>🔒</Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
                {tr("lockedAnomalies", "Login to view all anomalies")}
              </Typography>
            </Box>
          )}

          {!lockedForGuest && nextToken && (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore}
              sx={{ mt: 1, textTransform: "none" }}
            >
              {loadingMore ? tr("loading", "Loading...") : tr("loadMore", "Load more")}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
